import React, { useState, useEffect } from 'react';
import {
  Users, Clock, CheckCircle, AlertCircle, ChevronRight, Search,
  Building, Phone, Globe, Calendar, MessageSquare, Shield, AlertTriangle,
  Eye, Check, X, Unlock, RefreshCw, Filter, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get admin key from localStorage (set during login)
const getAdminKey = () => localStorage.getItem('admin_key') || '';

export const AdminOnboardingPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadSubmissions();
  }, [filter]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/admin/submissions?status=${filter}`, {
        headers: { 'X-Admin-Key': getAdminKey() }
      });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
      }
    } catch (err) {
      console.error('Failed to load submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissionDetail = async (clientId) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/admin/submission/${clientId}`, {
        headers: { 'X-Admin-Key': getAdminKey() }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedSubmission(data);
      }
    } catch (err) {
      console.error('Failed to load detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const unlockWizard = async (clientId) => {
    const reason = prompt('Enter reason for unlocking:');
    if (!reason) return;

    try {
      const res = await fetch(`${API_URL}/api/onboarding/admin/unlock/${clientId}?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'X-Admin-Key': getAdminKey() }
      });
      if (res.ok) {
        alert('Wizard unlocked successfully');
        loadSubmissions();
        setSelectedSubmission(null);
      }
    } catch (err) {
      alert('Failed to unlock wizard');
    }
  };

  const filteredSubmissions = submissions.filter(s => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      s.client_name?.toLowerCase().includes(query) ||
      s.client_id?.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status) => {
    const styles = {
      'not_started': 'bg-gray-100 text-gray-700',
      'in_progress': 'bg-yellow-100 text-yellow-700',
      'submitted': 'bg-blue-100 text-blue-700',
      'locked': 'bg-green-100 text-green-700'
    };
    const labels = {
      'not_started': 'Not Started',
      'in_progress': 'In Progress',
      'submitted': 'Submitted',
      'locked': 'Locked'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles['not_started']}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Onboarding Submissions</h1>
          <p className="text-gray-400">Review customer setup data for agent building</p>
        </div>
        <button
          onClick={loadSubmissions}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Total"
          value={submissions.length}
          icon={Users}
          color="blue"
        />
        <StatCard
          label="In Progress"
          value={submissions.filter(s => s.wizard_status === 'in_progress').length}
          icon={Clock}
          color="yellow"
        />
        <StatCard
          label="Submitted"
          value={submissions.filter(s => s.wizard_status === 'submitted').length}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          label="Locked"
          value={submissions.filter(s => s.wizard_status === 'locked').length}
          icon={Shield}
          color="purple"
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or ID..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
        >
          <option value="all">All Status</option>
          <option value="in_progress">In Progress</option>
          <option value="submitted">Submitted</option>
        </select>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Submissions List */}
        <div className="col-span-1 bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-700">
            <h2 className="font-semibold text-white">Submissions ({filteredSubmissions.length})</h2>
          </div>
          <div className="divide-y divide-gray-700 max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                No submissions found
              </div>
            ) : (
              filteredSubmissions.map(sub => (
                <div
                  key={sub.client_id}
                  onClick={() => loadSubmissionDetail(sub.client_id)}
                  className={`p-4 cursor-pointer hover:bg-gray-700 transition-colors ${
                    selectedSubmission?.wizard?.client_id === sub.client_id ? 'bg-gray-700' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-white">{sub.client_name || 'Unnamed'}</span>
                    {getStatusBadge(sub.wizard_status)}
                  </div>
                  <div className="text-sm text-gray-400">
                    {sub.industry_selection?.primary_industry && (
                      <span className="capitalize">{sub.industry_selection.primary_industry.replace('_', ' ')}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Updated: {new Date(sub.updated_at).toLocaleDateString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Detail View */}
        <div className="col-span-2 bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          {detailLoading ? (
            <div className="p-8 text-center text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading details...
            </div>
          ) : selectedSubmission ? (
            <SubmissionDetail
              data={selectedSubmission}
              onUnlock={() => unlockWizard(selectedSubmission.wizard.client_id)}
            />
          ) : (
            <div className="p-8 text-center text-gray-400">
              <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Select a submission to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    green: 'bg-green-500/20 text-green-400',
    purple: 'bg-purple-500/20 text-purple-400'
  };

  return (
    <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

const SubmissionDetail = ({ data, onUnlock }) => {
  const { wizard, client, industries, call_objectives, tone_options } = data;
  
  if (!wizard) return null;

  const industry = industries?.[wizard.industry_selection?.primary_industry];
  const tone = tone_options?.find(t => t.id === wizard.brand_voice?.tone);

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-gray-800">
        <div>
          <h2 className="text-xl font-bold text-white">{wizard.client_name || client?.name}</h2>
          <p className="text-sm text-gray-400">{client?.email}</p>
        </div>
        <div className="flex items-center gap-2">
          {wizard.wizard_status === 'locked' && (
            <button
              onClick={onUnlock}
              className="flex items-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
            >
              <Unlock className="w-4 h-4" />
              Unlock
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Business Profile */}
        {wizard.business_profile && (
          <DetailSection title="Business Profile" icon={Building}>
            <DetailGrid>
              <DetailItem label="Legal Name" value={wizard.business_profile.legal_name} />
              <DetailItem label="Display Name" value={wizard.business_profile.display_name} />
              <DetailItem label="Phone" value={wizard.business_profile.primary_phone} />
              <DetailItem label="Website" value={wizard.business_profile.website} />
              <DetailItem label="Address" value={wizard.business_profile.address} />
              <DetailItem label="City, State" value={`${wizard.business_profile.city}, ${wizard.business_profile.state}`} />
              <DetailItem label="Timezone" value={wizard.business_profile.timezone} />
              <DetailItem label="After Hours" value={wizard.business_profile.after_hours_behavior} />
            </DetailGrid>
            {wizard.business_profile.business_hours && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Business Hours</h4>
                <div className="grid grid-cols-7 gap-2 text-sm">
                  {Object.entries(wizard.business_profile.business_hours).map(([day, hours]) => (
                    <div key={day} className="text-center">
                      <div className="text-gray-500 capitalize">{day.slice(0, 3)}</div>
                      <div className={`text-xs ${hours.closed ? 'text-red-400' : 'text-green-400'}`}>
                        {hours.closed ? 'Closed' : `${hours.open}-${hours.close}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DetailSection>
        )}

        {/* Industry */}
        {wizard.industry_selection && (
          <DetailSection title="Industry" icon={Globe}>
            <DetailGrid>
              <DetailItem label="Primary" value={industry?.name || wizard.industry_selection.primary_industry} />
              <DetailItem label="Type" value={wizard.industry_selection.secondary_industry} />
              {wizard.industry_selection.other_description && (
                <DetailItem label="Description" value={wizard.industry_selection.other_description} full />
              )}
            </DetailGrid>
          </DetailSection>
        )}

        {/* Call Objectives */}
        {wizard.call_objectives?.objectives && (
          <DetailSection title="Call Objectives" icon={Phone}>
            <div className="flex flex-wrap gap-2">
              {wizard.call_objectives.objectives.map(objId => {
                const obj = call_objectives?.find(o => o.id === objId);
                return (
                  <span key={objId} className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
                    {obj?.icon} {obj?.label || objId}
                  </span>
                );
              })}
            </div>
          </DetailSection>
        )}

        {/* AI Permissions */}
        {wizard.ai_permissions && (
          <DetailSection title="AI Permissions" icon={Shield}>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(wizard.ai_permissions).map(([key, value]) => {
                if (key === 'price_disclaimer') return null;
                return (
                  <div key={key} className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg">
                    <span className="text-sm text-gray-300 capitalize">{key.replace(/_/g, ' ')}</span>
                    {value ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <X className="w-4 h-4 text-red-400" />
                    )}
                  </div>
                );
              })}
            </div>
          </DetailSection>
        )}

        {/* Appointment Details */}
        {wizard.appointment_details && wizard.ai_permissions?.can_book_appointments && (
          <DetailSection title="Appointment Details" icon={Calendar}>
            <DetailGrid>
              <DetailItem label="Max Per Day" value={wizard.appointment_details.max_per_day} />
              <DetailItem label="Same Day" value={wizard.appointment_details.same_day_allowed ? 'Yes' : 'No'} />
              <DetailItem label="Confirmation" value={wizard.appointment_details.confirmation_message} full />
              <DetailItem label="Cancellation" value={wizard.appointment_details.cancellation_policy} full />
            </DetailGrid>
            {wizard.appointment_details.appointment_types?.length > 0 && (
              <div className="mt-3">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Appointment Types</h4>
                <div className="space-y-1">
                  {wizard.appointment_details.appointment_types.map((apt, idx) => (
                    <div key={idx} className="flex justify-between text-sm p-2 bg-gray-700/50 rounded">
                      <span className="text-white">{apt.name || 'Unnamed'}</span>
                      <span className="text-gray-400">{apt.duration_minutes} min</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DetailSection>
        )}

        {/* Emergency Handling */}
        {wizard.emergency_handling && (
          <DetailSection title="Emergency Handling" icon={AlertTriangle}>
            <DetailGrid>
              <DetailItem label="Emergency Definition" value={wizard.emergency_handling.emergency_definition} full />
              <DetailItem label="Emergency Phone" value={wizard.emergency_handling.emergency_phone} />
              <DetailItem label="Fully Booked" value={wizard.emergency_handling.fully_booked_message} full />
              <DetailItem label="Outside Hours" value={wizard.emergency_handling.outside_hours_message} full />
              <DetailItem label="Unsure Response" value={wizard.emergency_handling.unsure_response} full />
              <DetailItem label="Fallback" value={wizard.emergency_handling.fallback_message} full />
            </DetailGrid>
          </DetailSection>
        )}

        {/* Brand Voice */}
        {wizard.brand_voice && (
          <DetailSection title="Brand Voice" icon={MessageSquare}>
            <DetailGrid>
              <DetailItem label="Tone" value={tone?.label || wizard.brand_voice.tone} />
              <DetailItem label="Greeting" value={wizard.brand_voice.greeting_message} full />
              {wizard.brand_voice.forbidden_phrases?.length > 0 && (
                <div className="col-span-2">
                  <span className="text-sm text-gray-400">Forbidden Phrases:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {wizard.brand_voice.forbidden_phrases.map((phrase, idx) => (
                      <span key={idx} className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm">
                        {phrase}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </DetailGrid>
          </DetailSection>
        )}

        {/* Submission Info */}
        <DetailSection title="Submission Info" icon={CheckCircle}>
          <DetailGrid>
            <DetailItem label="Status" value={wizard.wizard_status} />
            <DetailItem label="Version" value={wizard.submission_version} />
            <DetailItem label="Submitted" value={wizard.submission_timestamp ? new Date(wizard.submission_timestamp).toLocaleString() : 'Not yet'} />
            <DetailItem label="Last Updated" value={new Date(wizard.updated_at).toLocaleString()} />
          </DetailGrid>
        </DetailSection>
      </div>
    </div>
  );
};

const DetailSection = ({ title, icon: Icon, children }) => (
  <div className="bg-gray-700/30 rounded-lg p-4">
    <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-blue-400" />
      {title}
    </h3>
    {children}
  </div>
);

const DetailGrid = ({ children }) => (
  <div className="grid grid-cols-2 gap-3">
    {children}
  </div>
);

const DetailItem = ({ label, value, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <span className="text-xs text-gray-400 block">{label}</span>
    <span className="text-sm text-white">{value || '—'}</span>
  </div>
);

export default AdminOnboardingPage;
